import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Flex,
  Text,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Button,
  HStack,
  useDisclosure,
  Spinner,
} from '@chakra-ui/react';
import { RiFileList3Line } from 'react-icons/ri';
import { FunnelSimple } from 'phosphor-react';
import moment from 'moment';
import { PageBox } from '../../../../../../components/pageBox';
import { ReturnButton } from '../../../../../../components/buttons/ReturnButton';
import { MonthNavigator } from '../../../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../../../hooks/useMonthFilter';
import { useIcms } from '../../../../../../hooks/useIcms';
import { LivrosResumoCards } from '../components/LivrosResumoCards';
import { LivrosTable } from '../components/LivrosTable';
import { LivrosTotaisDrawer } from '../components/LivrosTotaisDrawer';
import { LivroTipoSingular } from '../../../../../../types/icms';

const TIPOS: {
  label: string;
  tipo: LivroTipoSingular;
  rota: 'entradas' | 'saidas' | 'servicos';
}[] = [
  { label: 'Entradas', tipo: 'entrada', rota: 'entradas' },
  { label: 'Saídas', tipo: 'saida', rota: 'saidas' },
  { label: 'Serviços', tipo: 'servico', rota: 'servicos' },
];

export function IcmsLivrosFiscaisEmpresaPage() {
  const { codiEmp } = useParams<{ codiEmp: string }>();
  const codiEmpNum = Number(codiEmp);
  const { getLivroFiscal, getLivrosFiscaisResumo } = useIcms();

  const { currentDate, dateStart, dateEnd, handlePrevMonth, handleNextMonth, handleSelectMonth } =
    useMonthFilter({ defaultToPreviousMonth: true });

  const [searchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const totaisDrawer = useDisclosure();

  const tipoAtivo = TIPOS[tabIndex];
  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '50', 10);

  const { data: resumoData, isLoading: isLoadingResumo } = useQuery({
    queryKey: ['livros-resumo', { codi_emp: codiEmpNum, dt_inicio: dateStart, dt_fim: dateEnd }],
    queryFn: getLivrosFiscaisResumo,
    enabled: !!codiEmpNum && !!dateStart && !!dateEnd,
  });

  const tableQuery = useQuery({
    queryKey: [
      'livro-fiscal',
      {
        codi_emp: codiEmpNum,
        tipo: tipoAtivo.rota,
        dt_inicio: dateStart,
        dt_fim: dateEnd,
        page,
        size,
      },
    ],
    queryFn: getLivroFiscal,
    enabled: !!codiEmpNum && !!dateStart && !!dateEnd,
    placeholderData: prev => prev,
  });

  const items = tableQuery.data?.data?.items ?? [];
  const pagination = useMemo(
    () => ({
      total: tableQuery.data?.data?.total ?? 0,
      pages: tableQuery.data?.data?.pages ?? 0,
      page: tableQuery.data?.data?.page ?? 1,
      size: tableQuery.data?.data?.size ?? size,
      next_page: tableQuery.data?.data?.next_page ?? null,
      previous_page: tableQuery.data?.data?.previous_page ?? null,
    }),
    [tableQuery.data, size],
  );

  return (
    <PageBox>
      <Flex align="center" pb={5}>
        <ReturnButton previousPage="/modulesHome/supervisor/icms/livros-fiscais" />
        <Box h="full">
          <Flex align="center" justify="center" borderRadius="full" p={3} bg="#E3F2FD">
            <RiFileList3Line color="#1976D2" className="size-8" />
          </Flex>
        </Box>
        <Flex direction="column" justify="center" gap={1} pl={2}>
          <Text fontWeight="bold" fontSize="16px">
            Livros Fiscais · Empresa #{codiEmpNum}
          </Text>
          <Text fontSize="12px" color="gray.500">
            Período: {moment(dateStart).format('DD/MM/YYYY')} a{' '}
            {moment(dateEnd).format('DD/MM/YYYY')}
          </Text>
        </Flex>
      </Flex>

      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <MonthNavigator
          currentDate={currentDate}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onSelectMonth={handleSelectMonth}
        />
        <HStack>
          <Button
            size="sm"
            leftIcon={<FunnelSimple size={16} />}
            variant="outline"
            onClick={totaisDrawer.onOpen}
          >
            Totais (CFOP / Acumulador)
          </Button>
        </HStack>
      </Flex>

      <LivrosResumoCards data={resumoData} isLoading={isLoadingResumo} />

      <Tabs colorScheme="blue" variant="enclosed" index={tabIndex} onChange={i => setTabIndex(i)}>
        <TabList>
          {TIPOS.map(t => (
            <Tab key={t.tipo} fontWeight="medium">
              {t.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {TIPOS.map(t => (
            <TabPanel key={t.tipo} px={0}>
              {tableQuery.isLoading && !tableQuery.data ? (
                <Flex justify="center" py={6}>
                  <Spinner color="primary" />
                </Flex>
              ) : (
                <LivrosTable
                  data={items}
                  pagination={pagination}
                  isFetching={tableQuery.isFetching}
                />
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {totaisDrawer.isOpen && (
        <LivrosTotaisDrawer
          isOpen={totaisDrawer.isOpen}
          onClose={totaisDrawer.onClose}
          codiEmp={codiEmpNum}
          tipo={tipoAtivo.tipo}
          dtInicio={dateStart}
          dtFim={dateEnd}
        />
      )}
    </PageBox>
  );
}
